import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowUpRight } from "lucide-react"
import { useBlogPosts } from "@/hooks/useBlog"
import { SectionSkeleton, ErrorState } from "@/components/state"
import { pageMeta } from "@/lib/seo"

export const Route = createFileRoute("/_public/blog/")({
  component: BlogIndex,
  head: () =>
    pageMeta({
      title: "Blog — Nexovia IT",
      description: "Engineering notes, product updates and ideas from Nexovia IT.",
      path: "/blog",
    }),
})

function BlogIndex() {
  const { data: posts, isLoading, isError } = useBlogPosts()
  const [featured, ...rest] = posts ?? []

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border bg-background">
        <div
          className="absolute inset-0 -z-10 opacity-60"
          style={{ background: "radial-gradient(ellipse at top, color-mix(in oklab, var(--rust-glow) 22%, transparent), transparent 60%)" }}
        />
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-28 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-rust">
            <span className="h-1.5 w-1.5 rounded-full bg-rust" /> Blog
          </span>
          <h1 className="mx-auto mt-6 max-w-3xl text-5xl md:text-7xl font-bold text-balance">
            Notes From <span className="text-rust">the Team.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            Engineering notes, product lessons and ideas from the people building Nexovia IT.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 md:py-28">
        {isLoading ? (
          <SectionSkeleton rows={6} className="md:grid-cols-2 lg:grid-cols-3" />
        ) : isError ? (
          <ErrorState message="Couldn't load blog posts right now." />
        ) : !posts || posts.length === 0 ? (
          <p className="text-center text-muted-foreground">No posts published yet.</p>
        ) : (
          <>
            {/* FEATURED POST */}
            <Link
              to="/blog/$slug"
              params={{ slug: featured.slug }}
              className="group grid gap-8 overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-card lg:grid-cols-2"
            >
              <div className="aspect-[16/10] overflow-hidden bg-secondary lg:aspect-auto">
                {featured.cover_image_url ? (
                  <img
                    src={featured.cover_image_url}
                    alt={featured.title}
                    width={1600}
                    height={1000}
                    className="h-full w-full object-cover object-top transition duration-500 group-hover:scale-105"
                  />
                ) : (
                  <div className="h-full w-full bg-gradient-hero" />
                )}
              </div>
              <div className="flex flex-col justify-center p-8 md:p-12">
                <span className="text-xs font-semibold uppercase tracking-widest text-rust">Latest post</span>
                {featured.published_at && (
                  <span className="mt-1 text-xs text-muted-foreground">
                    {new Date(featured.published_at).toLocaleDateString()}
                  </span>
                )}
                <h2 className="mt-4 text-3xl md:text-4xl font-bold text-balance">{featured.title}</h2>
                {featured.excerpt && <p className="mt-4 text-muted-foreground leading-relaxed">{featured.excerpt}</p>}
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-ink group-hover:text-rust">
                  Read article <ArrowUpRight className="h-4 w-4" />
                </span>
              </div>
            </Link>

            {/* REST OF POSTS */}
            {rest.length > 0 && (
              <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {rest.map((post) => (
                  <Link
                    key={post.id}
                    to="/blog/$slug"
                    params={{ slug: post.slug }}
                    className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-card"
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-secondary">
                      {post.cover_image_url ? (
                        <img
                          src={post.cover_image_url}
                          alt={post.title}
                          width={1600}
                          height={900}
                          className="h-full w-full object-cover object-top transition group-hover:scale-105"
                        />
                      ) : (
                        <div className="h-full w-full bg-gradient-hero" />
                      )}
                    </div>
                    <div className="flex flex-1 flex-col p-6">
                      {post.published_at && (
                        <span className="text-xs uppercase tracking-widest text-rust">
                          {new Date(post.published_at).toLocaleDateString()}
                        </span>
                      )}
                      <h3 className="mt-2 text-lg font-semibold">{post.title}</h3>
                      {post.excerpt && (
                        <p className="mt-2 flex-1 text-sm text-muted-foreground leading-relaxed">{post.excerpt}</p>
                      )}
                      <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-ink group-hover:text-rust">
                        Read more <ArrowUpRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-secondary/30">
        <div className="mx-auto max-w-5xl px-6 py-24 md:py-28 text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-rust">Have a project in mind?</p>
          <h2 className="mt-3 text-4xl md:text-5xl font-bold">Let's Build It Together.</h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            We turn ideas like these into shipped products. Tell us what you're working on.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
              Get in touch <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  )
}
